import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Menu,
  Tooltip,
} from '@mui/material';
import {
  Send,
  PushPin,
  PushPinOutlined,
  Edit,
  Delete,
  MoreVert,
  Lock,
  LockOpen,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { notesApi, Note, usersApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface CandidateNotesProps {
  candidateId: string;
}

const NOTE_CATEGORIES = [
  { value: 'general', label: 'General', chipColor: 'default' as const, buttonColor: 'inherit' as const },
  { value: 'interview', label: 'Interview', chipColor: 'primary' as const, buttonColor: 'primary' as const },
  { value: 'reference', label: 'Reference Check', chipColor: 'secondary' as const, buttonColor: 'secondary' as const },
  { value: 'evaluation', label: 'Evaluation', chipColor: 'warning' as const, buttonColor: 'warning' as const },
  { value: 'follow-up', label: 'Follow-up', chipColor: 'info' as const, buttonColor: 'info' as const },
];

const CandidateNotes: React.FC<CandidateNotesProps> = ({ candidateId }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');
  const [category, setCategory] = useState('general');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<{ id: number; content: string } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [mentions, setMentions] = useState<number[]>([]);
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionAnchor, setMentionAnchor] = useState<null | HTMLElement>(null);

  // Fetch notes
  const fetchNotes = async () => {
    try {
      setLoading(true);
      console.log('Fetching notes with category:', selectedCategory);
      const response = await notesApi.getCandidateNotes(candidateId, { 
        category: selectedCategory || undefined,
        includePrivate: user?.role === 'admin',
      });
      console.log('Notes received:', response.data);
      setNotes(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for mentions
  const fetchUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    console.log('useEffect triggered - candidateId:', candidateId, 'selectedCategory:', selectedCategory);
    fetchNotes();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, selectedCategory]);

  // Handle create note
  const handleCreateNote = async () => {
    if (!newNote.trim()) return;

    try {
      const response = await notesApi.createNote(candidateId, {
        content: newNote,
        category,
        is_private: isPrivate,
        is_pinned: isPinned,
        mentions,
      });
      
      // Process the new note to add mentions array if not present
      const newNoteData = response.data;
      if (!newNoteData.mentions) {
        newNoteData.mentions = [];
      }
      
      // Only add the note to the list if it matches the current filter
      if (!selectedCategory || selectedCategory === newNoteData.category) {
        // Add the new note to the existing notes list
        // If it's pinned, it should go at the top, otherwise after pinned notes
        if (newNoteData.is_pinned || notes.length === 0) {
          setNotes([newNoteData, ...notes]);
        } else {
          // Find the index after all pinned notes
          const firstUnpinnedIndex = notes.findIndex(note => !note.is_pinned);
          if (firstUnpinnedIndex === -1) {
            // All notes are pinned, add to end
            setNotes([...notes, newNoteData]);
          } else {
            // Insert after pinned notes
            const updatedNotes = [...notes];
            updatedNotes.splice(firstUnpinnedIndex, 0, newNoteData);
            setNotes(updatedNotes);
          }
        }
      } else {
        // Note was created but doesn't match current filter
        // Could optionally clear the filter or show a message
        // For now, just refetch to ensure consistency
        fetchNotes();
      }
      
      // Clear form
      setNewNote('');
      setMentions([]);
      setIsPrivate(false);
      setIsPinned(false);
      setCategory('general');
    } catch (err: any) {
      console.error('Error creating note:', err);
      setError('Failed to create note');
    }
  };

  // Handle update note
  const handleUpdateNote = async () => {
    if (!editingNote || !editingNote.content.trim()) return;

    try {
      await notesApi.updateNote(editingNote.id.toString(), {
        content: editingNote.content,
      });
      
      setEditingNote(null);
      fetchNotes();
    } catch (err: any) {
      console.error('Error updating note:', err);
      setError('Failed to update note');
    }
  };

  // Handle delete note
  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await notesApi.deleteNote(noteId.toString());
      fetchNotes();
    } catch (err: any) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note');
    }
  };

  // Handle toggle pin
  const handleTogglePin = async (note: Note) => {
    try {
      await notesApi.updateNote(note.id.toString(), {
        is_pinned: !note.is_pinned,
      });
      fetchNotes();
    } catch (err: any) {
      console.error('Error toggling pin:', err);
      setError('Failed to update note');
    }
  };

  // Handle toggle privacy
  const handleTogglePrivacy = async (note: Note) => {
    try {
      await notesApi.updateNote(note.id.toString(), {
        is_private: !note.is_private,
      });
      fetchNotes();
    } catch (err: any) {
      console.error('Error toggling privacy:', err);
      setError('Failed to update note');
    }
  };

  // Handle mention
  const handleMention = (userId: number) => {
    if (!mentions.includes(userId)) {
      setMentions([...mentions, userId]);
    }
    setShowMentionMenu(false);
    setMentionSearch('');
  };

  // Check for @ symbol to show mention menu
  const handleNoteChange = (value: string) => {
    setNewNote(value);
    
    // Check if user typed @
    if (value.endsWith('@')) {
      setShowMentionMenu(true);
      setMentionAnchor(document.activeElement as HTMLElement);
    }
  };

  // Filter users for mention menu
  const filteredUsers = users.filter(u => 
    u.id !== Number(user?.id) && 
    (u.first_name.toLowerCase().includes(mentionSearch.toLowerCase()) ||
     u.last_name.toLowerCase().includes(mentionSearch.toLowerCase()) ||
     u.email.toLowerCase().includes(mentionSearch.toLowerCase()))
  );

  // Get category config
  const getCategoryConfig = (categoryValue: string) => {
    return NOTE_CATEGORIES.find(c => c.value === categoryValue) || NOTE_CATEGORIES[0];
  };

  // Can user edit/delete note
  const canModifyNote = (note: Note) => {
    return note.user_id === Number(user?.id) || user?.role === 'admin';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-testid="candidate-notes">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Create new note */}
      <Paper sx={{ p: 3, mb: 3 }} data-testid="new-note-form">
        <Typography variant="h6" gutterBottom>
          Add a Note
        </Typography>
        
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Write a note about this candidate..."
          value={newNote}
          onChange={(e) => handleNoteChange(e.target.value)}
          sx={{ mb: 2 }}
          data-testid="note-input"
        />

        {/* Mention menu */}
        <Menu
          open={showMentionMenu}
          anchorEl={mentionAnchor}
          onClose={() => {
            setShowMentionMenu(false);
            setMentionSearch('');
          }}
          PaperProps={{
            style: {
              maxHeight: 200,
              width: '250px',
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <TextField
              size="small"
              placeholder="Search users..."
              value={mentionSearch}
              onChange={(e) => setMentionSearch(e.target.value)}
              autoFocus
              fullWidth
            />
          </Box>
          {filteredUsers.map((u) => (
            <MenuItem
              key={u.id}
              onClick={() => handleMention(u.id)}
            >
              {u.first_name} {u.last_name} ({u.email})
            </MenuItem>
          ))}
        </Menu>

        {/* Selected mentions */}
        {mentions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Mentioning:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {mentions.map((userId) => {
                const mentionedUser = users.find(u => u.id === userId);
                return mentionedUser ? (
                  <Chip
                    key={userId}
                    label={`@${mentionedUser.first_name} ${mentionedUser.last_name}`}
                    size="small"
                    onDelete={() => setMentions(mentions.filter(id => id !== userId))}
                  />
                ) : null;
              })}
            </Stack>
          </Box>
        )}

        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              label="Category"
              data-testid="category-select"
            >
              {NOTE_CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                data-testid="private-checkbox"
              />
            }
            label="Private"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
                data-testid="pinned-checkbox"
              />
            }
            label="Pin to top"
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            endIcon={<Send />}
            onClick={handleCreateNote}
            disabled={!newNote.trim()}
            data-testid="add-note-button"
          >
            Add Note
          </Button>
        </Stack>
      </Paper>

      {/* Filter by category */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant={selectedCategory === null ? 'contained' : 'outlined'}
          onClick={() => {
            console.log('All clicked, setting category to null');
            setSelectedCategory(null);
          }}
          data-testid="filter-all"
        >
          All
        </Button>
        {NOTE_CATEGORIES.map((cat) => (
          <Button
            key={cat.value}
            size="small"
            variant={selectedCategory === cat.value ? 'contained' : 'outlined'}
            onClick={() => {
              console.log('Category clicked:', cat.value);
              setSelectedCategory(cat.value);
            }}
            color={cat.buttonColor}
            data-testid={`filter-${cat.value}`}
          >
            {cat.label}
          </Button>
        ))}
      </Stack>

      {/* Notes list */}
      {notes.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {selectedCategory 
              ? `No ${getCategoryConfig(selectedCategory).label.toLowerCase()} notes yet.`
              : 'No notes yet. Add a note above to get started.'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2} data-testid="notes-list">
          {notes.map((note) => {
            const categoryConfig = getCategoryConfig(note.category || 'general');
            const isEditing = editingNote?.id === note.id;

            return (
              <Paper
                key={note.id}
                sx={{
                  p: 2,
                  ...(note.is_pinned && {
                    borderLeft: 4,
                    borderColor: 'primary.main',
                  }),
                }}
                data-testid={`note-item-${note.id}`}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    {/* Header */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">
                        {note.author_first_name} {note.author_last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                      </Typography>
                      <Chip
                        label={categoryConfig.label}
                        size="small"
                        color={categoryConfig.chipColor}
                      />
                      {note.is_private && (
                        <Tooltip title="Private note">
                          <Lock fontSize="small" color="action" />
                        </Tooltip>
                      )}
                      {note.is_pinned && (
                        <Tooltip title="Pinned">
                          <PushPin fontSize="small" color="primary" />
                        </Tooltip>
                      )}
                    </Stack>

                    {/* Content */}
                    {isEditing ? (
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={editingNote.content}
                          onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                          sx={{ mb: 1 }}
                        />
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={handleUpdateNote}>
                            Save
                          </Button>
                          <Button size="small" onClick={() => setEditingNote(null)}>
                            Cancel
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {note.content}
                      </Typography>
                    )}

                    {/* Mentions */}
                    {note.mentions && note.mentions.length > 0 && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {note.mentions.map((mention) => (
                          <Chip
                            key={mention.user_id}
                            label={`@${mention.name}`}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>

                  {/* Actions */}
                  {canModifyNote(note) && (
                    <Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setSelectedNote(note);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && Boolean(selectedNote)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedNote(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedNote) {
              setEditingNote({ id: selectedNote.id, content: selectedNote.content });
            }
            setAnchorEl(null);
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedNote) {
              handleTogglePin(selectedNote);
            }
            setAnchorEl(null);
          }}
        >
          {selectedNote?.is_pinned ? (
            <>
              <PushPinOutlined fontSize="small" sx={{ mr: 1 }} />
              Unpin
            </>
          ) : (
            <>
              <PushPin fontSize="small" sx={{ mr: 1 }} />
              Pin to top
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedNote) {
              handleTogglePrivacy(selectedNote);
            }
            setAnchorEl(null);
          }}
        >
          {selectedNote?.is_private ? (
            <>
              <LockOpen fontSize="small" sx={{ mr: 1 }} />
              Make public
            </>
          ) : (
            <>
              <Lock fontSize="small" sx={{ mr: 1 }} />
              Make private
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedNote) {
              handleDeleteNote(selectedNote.id);
            }
            setAnchorEl(null);
          }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CandidateNotes;